'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { Pill } from '@/components/ui/pill';
import { Typography } from '@/components/ui/typography';
import { Card } from '@/components/ui/card';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';
import {
  ClipboardList,
  Rocket,
  Users,
  GraduationCap,
  TestTube,
  Headphones,
} from 'lucide-react';

export function DevelopmentProcessSection() {
  const t = useTranslations('aboutPage.developmentProcess');
  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  const stepIcons = [
    ClipboardList, // Planning & gathering requirements
    Rocket,         // Project Launch
    Users,          // Management
    GraduationCap,  // Technique instruction
    TestTube,       // User acceptance testing
    Headphones,     // Live Now - Ongoing support
  ];

  const steps = [
    {
      title: t('step1.title'),
      description: t('step1.description'),
      icon: stepIcons[0],
    },
    {
      title: t('step2.title'),
      description: t('step2.description'),
      icon: stepIcons[1],
    },
    {
      title: t('step3.title'),
      description: t('step3.description'),
      icon: stepIcons[2],
    },
    {
      title: t('step4.title'),
      description: t('step4.description'),
      icon: stepIcons[3],
    },
    {
      title: t('step5.title'),
      description: t('step5.description'),
      icon: stepIcons[4],
    },
    {
      title: t('step6.title'),
      description: t('step6.description'),
      icon: stepIcons[5],
    },
  ];

  return (
    <SectionWrapper
      ref={sectionRef.ref}
      variant="default"
      padding="md"
      className="relative overflow-hidden"
    >
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="container relative z-10 max-w-7xl mx-auto">
        <div className={cn(
          "mb-12 text-center transition-all duration-700",
          sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        )}>
          {t('pill') && (
            <div className="mb-4 flex justify-center">
              <Pill variant="default" className="text-gray-900 dark:text-white">
                {t('pill')}
              </Pill>
            </div>
          )}
          <SectionHeading
            page=""
            align="center"
            fallbackTitle={t('title')}
            fallbackSubtitle={t('subtitle')}
          />
        </div>

        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, index) => {
              const IconComponent = step.icon;

              return (
                <div key={index} className="relative">
                  <Card
                    title={step.title}
                    description={step.description}
                    icon={IconComponent}
                    showFooterLink={false}
                    animationDelay={200 + index * 100}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-primary-500/50"
                    renderContent={
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-primary-500/10 dark:bg-primary-500/20 flex items-center justify-center relative">
                            <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-primary-500 text-white text-xs font-bold flex items-center justify-center">
                              {index + 1}
                            </span>
                            <IconComponent className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <Typography variant="h4" className="mb-2 text-gray-900 dark:text-white">
                            {step.title}
                          </Typography>
                          <Typography variant="p" className="text-gray-600 dark:text-gray-400 leading-relaxed">
                            {step.description}
                          </Typography>
                        </div>
                      </div>
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

